import { Button } from 'components/atoms/button'
import { useRef } from 'react'
import QRCode from 'react-qr-code'
import { IconSvg } from './IconSvg'
import clsx from 'clsx'

interface QRCodeWrapperProps {
	value: string
	size?: number
	bgColor?: string
	fgColor?: string
	level?: 'L' | 'M' | 'Q' | 'H'
	showQRCode?: boolean
	showDownloadButton?: boolean
	downloadFileName?: string
	className?: string
	qrClassName?: string
	buttonClassName?: string
	buttonText?: string
}

export function QRCodeWrapper({
	value,
	size = 256,
	bgColor = '#FFFFFF',
	fgColor = '#000000',
	level = 'L',
	showQRCode = true,
	showDownloadButton = true,
	downloadFileName = 'qrcode',
	className = '',
	qrClassName = '',
	buttonClassName = '',
	buttonText = 'Download QR',
}: QRCodeWrapperProps) {
	const qrRef = useRef<HTMLDivElement>(null)

	const handleDownload = () => {
		if (!qrRef.current) return

		const svg = qrRef.current.querySelector('svg')
		if (!svg) return

		// Use higher resolution for better quality (4x the display size)
		const scale = 4
		const canvasSize = size * scale

		// Create a canvas element
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		// Set canvas dimensions to high resolution
		canvas.width = canvasSize
		canvas.height = canvasSize

		// Convert SVG to data URL
		const svgData = new XMLSerializer().serializeToString(svg)
		const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
		const url = URL.createObjectURL(svgBlob)

		// Create an image from the SVG
		const img = new Image()
		img.onload = () => {
			// Draw the image on canvas at higher resolution
			ctx.drawImage(img, 0, 0, canvasSize, canvasSize)

			// Convert canvas to blob
			canvas.toBlob(blob => {
				if (!blob) return

				// Create download link
				const downloadUrl = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = downloadUrl
				link.download = `${downloadFileName}.png`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)

				// Clean up
				URL.revokeObjectURL(downloadUrl)
				URL.revokeObjectURL(url)
			}, 'image/png')
		}
		img.src = url
	}

	return (
		<div className={`flex flex-col items-center gap-4 ${className}`}>
			<div
				ref={qrRef}
				className={qrClassName}
				style={showQRCode ? undefined : { position: 'absolute', left: '-9999px' }}
			>
				<QRCode
					size={size}
					style={{
						height: 'auto',
						maxWidth: '100%',
						width: '100%',
					}}
					value={value}
					bgColor={bgColor}
					fgColor={fgColor}
					level={level}
					viewBox={`0 0 ${size} ${size}`}
				/>
			</div>

			{showDownloadButton && (
				<Button
					variant="secondary"
					size="lg"
					onClick={handleDownload}
					className={clsx(buttonClassName, 'flex items-center gap-1')}
				>
					{buttonText}
					<IconSvg icon="download" className="text-primary-text" />
				</Button>
			)}
		</div>
	)
}
